import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import pluralize from 'pluralize';
import { ContentLoading, ErrorPage } from 'axway-react-ui';
import browserHistory from 'components/utility/history';
import ModelDesignInitiator from './ModelDesignInitiator';
import ModelDesignView from '../view/ModelDesignView';
import * as actions from '../actions';
import ModelOps from '../view/ModelOps';

class ModelDesign extends React.Component {
	constructor(props) {
		super(props);
		this.okDesignInitiator = this.okDesignInitiator.bind(this);
		this.cancelDesignInitiator = this.cancelDesignInitiator.bind(this);
		this.onSave = this.onSave.bind(this);
		this.onCancel = this.onCancel.bind(this);
		this.updateModelDef = this.updateModelDef.bind(this);
		this.onChange = this.onChange.bind(this);

		this.state = {
			modelDef: null
		};
	}

	/**
	 * Initialize the designers model definition.
	 */
	componentWillMount() {
		this.intializeModelDef(this.props);
	}

	componentWillUnmount() {
		const { actions: { designModelCancel } } = this.props;
		designModelCancel();
	}

	/**
	 * Initialize the designers model definition.
	 */
	componentWillReceiveProps(props) {
		this.intializeModelDef(props);
	}

	hasPrimaryKey(model) {
		// Defaults to true
		let hasPk = true;
		if (model.metadata && model.metadata.primarykey === null) {
			hasPk = false;
		}
		return hasPk;
	}

	isAutogeneratedPrimaryKey(model) {
		// Defaults to true
		let autogenerated = true;
		if (model.metadata && model.metadata.primaryKeyDetails && model.metadata.primaryKeyDetails.hasOwnProperty('autogenerated')) {
			autogenerated = model.metadata.primaryKeyDetails.autogenerated;
		}
		return autogenerated;
	}

	getPrimaryKeyType(model) {
		// Defaults to undefined
		let type;
		if (model.metadata && model.metadata.primaryKeyDetails && model.metadata.primaryKeyDetails.hasOwnProperty('type')) {
			type = model.metadata.primaryKeyDetails.type;
		}
		return type;
	}

	intializeModelDef(props) {
		const { mode, queryState, modelParent,
			modelName, modelDescription, modelConnector } = props.initial;
		let modelSource = props.initial.modelSource;

		if (modelSource) {
			if (typeof modelSource === 'string') {
				modelSource = this.props.models[modelSource];
			}
		}

		if ((mode === 'edit' && !modelSource)
			|| (mode === 'compose' && (!modelParent || !modelName))
			|| (mode === 'create' && !modelName)) {
			// Need to request the initial data if not already requested
			!queryState && this.triggerDesignInitiator(props);
		} else if (!this.state.modelDef || !this.state.modelDef.name) {
			// Only re-initialize if not alread initialized
			const modelDef = modelSource
				? JSON.parse(JSON.stringify(modelSource)) : {
					_joinmeta_: {
						hasJoin: false
					},
					name: '',
					description: '',
					connector: {},
					fields: {},
					actions: [],
					metadata: {}
				};

			if (!modelSource) {
				modelParent && (modelDef.parent = modelParent);
				modelName && (modelDef.name = modelName);
				modelDescription && (modelDef.description = modelDescription);
				modelConnector && (modelDef.connector = { name: modelConnector });
			}

			if (mode === 'compose' && modelParent
				&& Object.keys(this.props.models).length !== 0
				&& this.props.models[modelParent])			{
				const parent = this.props.models[modelParent];
				// clone the parent's primary key and related metadata
				if (this.hasPrimaryKey(parent)) {
					modelDef.metadata.primarykey = parent.metadata && parent.metadata.primarykey || 'id';
					modelDef.metadata.primaryKeyDetails = {
						autogenerated: this.isAutogeneratedPrimaryKey(parent),
						type: this.getPrimaryKeyType(parent)
					};
				} else {
					modelDef.metadata.primarykey = null;
				}

				// clone the parent's fields and add a `model` reference to parent.
				Object.keys(parent.fields).forEach(key => {
					const parentField = Object.assign({},
						parent.fields[key],
						{ model: modelParent }
					);
					// set field.name to mark it as inherited. Used in composite connector
					parentField.name = key;
					ModelOps.addField(modelDef, key, parentField);
				});
			}

			if (mode === 'compose' || mode === 'create') {
				modelDef.singular = !modelDef.singular ? modelDef.name : null;
				modelDef.plural = !modelDef.plural ? pluralize(modelDef.name) : null;
				modelDef.actions = [];
			}
			this.setState({ modelDef });
		}
	}

	onChange(modelDef) {
		if (this.state.modelDef.name !== modelDef.name) {
			modelDef.singular = modelDef.name;
			modelDef.plural = pluralize(modelDef.name);
		}
		this.setState({ modelDef });
	}

	onSave() {
		const modelDef = this.state.modelDef;
		(modelDef.singular === modelDef.name) && delete modelDef.singular;
		(modelDef.plural === pluralize(modelDef.name)) && delete modelDef.plural;
		this.props.actions.saveModel({
			model: modelDef
		});
	}

	onCancel() {
		browserHistory.push(`${window.$conf.prefix}/project/models`);
	}

	triggerDesignInitiator(props) {
		this.props.actions.designModelInitState({
			...props.initial,
			queryState: true
		});
	}

	cancelDesignInitiator() {
		this.props.actions.designModelCancel();
		this.onCancel();
	}

	okDesignInitiator(values) {
		let modelSource = null;
		if (values.modelSource) {
			if (typeof values.modelSource === 'string') {
				modelSource = this.props.models[values.modelSource];
			} else {
				modelSource = values.modelSource;
			}
		}
		this.props.actions.designModelInitState({
			...this.props.initial,
			...values,
			modelSource,
			queryState: false
		});
	}

	updateModelDef(update) {
		const updateKey = Object.keys(update)[0];
		const newState = {
			...this.state,
			modelDef: {
				...this.state.modelDef,
				...update
			}
		};
		const isEmptyResponseKey = (updateKey === 'singular' || updateKey === 'plural') && update[updateKey] === '';
		if (isEmptyResponseKey) {
			delete newState.modelDef[updateKey];
		}
		this.setState(newState);
	}

	render() {
		const { initial, error, errorDetail, loading, models, connectors } = this.props;
		if (error) {
			return (
				<ErrorPage errorDetail={errorDetail} />
			);
		} else if (this.state.modelDef && this.state.modelDef.generated) {
			// Can't edit a generated model
			return (
				<ErrorPage errorDetail={{ action: 'EDIT_GENERATED_MODEL' }} />
			);
		} else if (this.state.modelDef && this.state.modelDef._supermodel) {
			// Can't edit a legacy extended/reduced model, i.e. one that has a _supermodel
			return (
				<ErrorPage errorDetail={{ action: 'EDIT_LEGACY_REDUCE_EXTEND_MODEL' }} />
			);
		} else if (this.state.modelDef && this.state.modelDef._joinmeta_
				&& this.state.modelDef._joinmeta_.error) {
			// Can't edit an unsupported composite model
			return (
				<ErrorPage errorDetail={{ action: 'EDIT_UNSUPPORTED_COMPOSITE_MODEL' }} />
			);
		} else if (initial && initial.mode && !loading) {
			return (
				<div>
					<ModelDesignInitiator
						onOkay={this.okDesignInitiator}
						onClose={this.cancelDesignInitiator}
					/>
					<ModelDesignView
						modelDef={this.state.modelDef}
						mode={initial.mode}
						onChange={this.onChange}
						onSave={this.onSave}
						onCancel={this.onCancel}
						connectors={connectors}
						models={models}
						updateModelDef={this.updateModelDef}
					/>
				</div>
			);
		}
		return <ContentLoading />;
	}
}

function mapDispatchToProps(dispatch) {
	return {
		actions: bindActionCreators(actions, dispatch)
	};
}

function mapStateToProps(state) {
	return {
		loading: state.models.loading,
		error: state.models.error,
		errorDetail: state.models.errorDetail,
		initial: state.models.initial,
		models: state.models.models || {},
		connectors: state.connectors.connectors
	};
}

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(ModelDesign);
